import {
  CreateBillPaymentV3Req,
  CreateBillPaymentV3Res,
  EmailBillPaymentPDFReq,
  EmailBillPaymentPDFRes,
  EmailCreditHistoryPDFReq,
  EmailCreditHistoryPDFRes,
  EmailFinanceChargePDFReq,
  EmailFinanceChargePDFRes,
  GetBillPaymentPDFReq,
  GetBillPaymentPDFRes,
  GetBillPaymentReq,
  GetBillPaymentRes,
  GetCreditHistoryPDFReq,
  GetCreditHistoryPDFRes,
  GetCreditHistoryReq,
  GetCreditHistoryRes,
  GetFinanceChargePDFReq,
  GetFinanceChargePDFRes,
  GetFinanceChargeReq,
  GetFinanceChargeRes,
  GetNonFullyAllocatedCreditHistoryCountReq,
  GetNonFullyAllocatedCreditHistoryCountRes,
  RefundBillPaymentReq,
  RefundBillPaymentRes,
  UpdateBillPaymentDateReq,
  UpdateBillPaymentDateRes,
  CalculateBillPaymentCardSurchargeReq,
  CalculateBillPaymentCardSurchargeRes,
  GetIsVoidingBillPaymentAllowedReq,
  GetIsVoidingBillPaymentAllowedRes,
  VoidBillPaymentReq,
  VoidBillPaymentRes,
  GetIsEditingBillPaymentDateAllowedReq,
  GetIsEditingBillPaymentDateAllowedRes,
  VoidFinanceChargeReq,
  GetIsVoidingFinanceChargeAllowedRes,
  GetIsVoidingFinanceChargeAllowedReq,
  VoidFinanceChargeRes,
  ReallocateBillPaymentReq,
  ReallocateBillPaymentRes,
  GetIsReallocatingBillPaymentAllowedReq,
  GetIsReallocatingBillPaymentAllowedRes,
} from "gen/api/billpayment/service_pb"
import { BillPaymentServiceClient } from "gen/api/billpayment/service_pb.client"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { UUID } from "shared-ts/classes/uuid"

import { apiSlice, transport } from "./apiSlice"

const b = new BillPaymentServiceClient(transport)

export const BillFixedCacheKeys = {
  CREATE_BILL: "shared-create-bill",
} as const

export const billsApiSlice = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "Bill",
      "BillPDF",
      "Customer",
      "CustomerCards",
      "Location",
      "FinanceCharge",
      "CreditHistory",
      "NonFullyAllocatedCreditHistoryCount",
    ],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getBill: builder.query<GetBillPaymentRes, GetBillPaymentReq>({
        query: (r) => b.getBillPayment(r),
        providesTags: (_response, _error, request) =>
          request.id
            ? [
                {
                  type: "Bill",
                  id: UUID.idString(request.id),
                },
              ]
            : [],
      }),
      createBillV3: builder.mutation<
        CreateBillPaymentV3Res,
        CreateBillPaymentV3Req
      >({
        query: (r) => b.createBillPaymentV3(r),
        invalidatesTags: (_result, _error, arg) => [
          { type: "Location" },
          { type: "Customer", id: UUID.idString(arg.customerId) },
          { type: "CustomerCards", id: UUID.idString(arg.customerId) },
          { type: "NonFullyAllocatedCreditHistoryCount" },
        ],
      }),
      getBillPDF: builder.query<GetBillPaymentPDFRes, GetBillPaymentPDFReq>({
        query: (r) => b.getBillPaymentPDF(r),
        providesTags: (_response, _error, request) =>
          request.id
            ? [
                {
                  type: "BillPDF",
                  id: UUID.idString(request.id),
                },
              ]
            : [],
      }),
      emailBillPDF: builder.mutation<
        EmailBillPaymentPDFRes,
        EmailBillPaymentPDFReq
      >({
        query: (r) => b.emailBillPaymentPDF(r),
      }),

      refundBill: builder.mutation<
        RefundBillPaymentRes,
        RefundBillPaymentReq & { _customerId: PBUUID }
      >({
        query: ({ _customerId, ...r }) => b.refundBillPayment(r),
        invalidatesTags: (_response, _error, request) =>
          request.id
            ? [
                { type: "Location" },
                { type: "Bill", id: UUID.idString(request.id) },
                { type: "BillPDF", id: UUID.idString(request.id) },
                {
                  type: "Customer",
                  id: UUID.fromPB(request._customerId).toString(),
                },
              ]
            : [],
      }),
      getIsVoidingBillPaymentAllowed: builder.query<
        GetIsVoidingBillPaymentAllowedRes,
        GetIsVoidingBillPaymentAllowedReq
      >({
        query: (r) => b.getIsVoidingBillPaymentAllowed(r),
      }),
      voidBill: builder.mutation<VoidBillPaymentRes, VoidBillPaymentReq>({
        query: (r) => b.voidBillPayment(r),
      }),
      getIsEditingBillPaymentDateAllowed: builder.query<
        GetIsEditingBillPaymentDateAllowedRes,
        GetIsEditingBillPaymentDateAllowedReq
      >({
        query: (r) => b.getIsEditingBillPaymentDateAllowed(r),
      }),
      updateBillDate: builder.mutation<
        UpdateBillPaymentDateRes,
        UpdateBillPaymentDateReq
      >({
        query: (r) => b.updateBillPaymentDate(r),
        invalidatesTags: (_res, _error, req) => [
          { type: "Bill", id: UUID.idString(req.id) },
          { type: "BillPDF", id: UUID.idString(req.id) },
        ],
      }),
      reallocateBillPayment: builder.mutation<
        ReallocateBillPaymentRes,
        ReallocateBillPaymentReq
      >({
        query: (r) => b.reallocateBillPayment(r),
        invalidatesTags: (_res, _error, req) => [
          { type: "Bill", id: UUID.idString(req.billId) },
          { type: "BillPDF", id: UUID.idString(req.billId) },
        ],
      }),
      getIsReallocatingBillPaymentAllowed: builder.query<
        GetIsReallocatingBillPaymentAllowedRes,
        GetIsReallocatingBillPaymentAllowedReq
      >({
        query: (r) => b.getIsReallocatingBillPaymentAllowed(r),
      }),

      calculateBillPaymentCardSurcharge: builder.query<
        CalculateBillPaymentCardSurchargeRes,
        CalculateBillPaymentCardSurchargeReq
      >({
        query: (r) => b.calculateBillPaymentCardSurcharge(r),
      }),
      // Finance charges
      getFinanceCharge: builder.query<GetFinanceChargeRes, GetFinanceChargeReq>(
        {
          query: (r) => b.getFinanceCharge(r),
          providesTags: (_response, _error, request) =>
            request.id
              ? [
                  {
                    type: "FinanceCharge",
                    id: UUID.idString(request.id),
                  },
                ]
              : [],
        }
      ),
      getFinanceChargePDF: builder.query<
        GetFinanceChargePDFRes,
        GetFinanceChargePDFReq
      >({
        query: (r) => b.getFinanceChargePDF(r),
      }),

      emailFinanceChargePDF: builder.mutation<
        EmailFinanceChargePDFRes,
        EmailFinanceChargePDFReq
      >({
        query: (r) => b.emailFinanceChargePDF(r),
      }),
      getIsVoidingFinanceChargeAllowed: builder.query<
        GetIsVoidingFinanceChargeAllowedRes,
        GetIsVoidingFinanceChargeAllowedReq
      >({
        query: (r) => b.getIsVoidingFinanceChargeAllowed(r),
      }),
      voidFinanceCharge: builder.mutation<
        VoidFinanceChargeRes,
        VoidFinanceChargeReq
      >({
        query: (r) => b.voidFinanceCharge(r),
      }),
      // Credit history
      getCreditHistory: builder.query<GetCreditHistoryRes, GetCreditHistoryReq>(
        {
          query: (r) => b.getCreditHistory(r),
          providesTags: (_response, _error, request) =>
            request.id
              ? [{ type: "CreditHistory", id: UUID.idString(request.id) }]
              : [],
        }
      ),
      getCreditHistoryPDF: builder.query<
        GetCreditHistoryPDFRes,
        GetCreditHistoryPDFReq
      >({
        query: (r) => b.getCreditHistoryPDF(r),
      }),
      emailCreditHistoryPDF: builder.mutation<
        EmailCreditHistoryPDFRes,
        EmailCreditHistoryPDFReq
      >({
        query: (r) => b.emailCreditHistoryPDF(r),
      }),
      getNonFullyAllocatedCreditHistoryCount: builder.query<
        GetNonFullyAllocatedCreditHistoryCountRes,
        GetNonFullyAllocatedCreditHistoryCountReq
      >({
        query: (r) => b.getNonFullyAllocatedCreditHistoryCount(r),
        providesTags: ["NonFullyAllocatedCreditHistoryCount"],
      }),
    }),
  })

export const {
  // Bills
  useGetIsVoidingBillPaymentAllowedQuery,
  useVoidBillMutation,
  useGetIsEditingBillPaymentDateAllowedQuery,
  useGetBillQuery,
  useLazyGetBillQuery,
  useCreateBillV3Mutation,
  useGetBillPDFQuery,
  useLazyGetBillPDFQuery,
  useEmailBillPDFMutation,
  useRefundBillMutation,
  useUpdateBillDateMutation,
  useCalculateBillPaymentCardSurchargeQuery,
  useReallocateBillPaymentMutation,
  useGetIsReallocatingBillPaymentAllowedQuery,
  // Finance charges
  useGetFinanceChargeQuery,
  useGetFinanceChargePDFQuery,
  useLazyGetFinanceChargePDFQuery,
  useEmailFinanceChargePDFMutation,
  useGetIsVoidingFinanceChargeAllowedQuery,
  useVoidFinanceChargeMutation,
  // Credits
  useGetCreditHistoryQuery,
  useGetCreditHistoryPDFQuery,
  useLazyGetCreditHistoryPDFQuery,
  useEmailCreditHistoryPDFMutation,
  useGetNonFullyAllocatedCreditHistoryCountQuery,
} = billsApiSlice
